import { useEffect, useState, useCallback } from "react";
import api, { qs, apiError } from "@/lib/api";
import { monthOptions, monthLabel, pctTone, fmtPct } from "@/lib/dpc";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, AlertTriangle, CheckCircle2 } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";

export default function DpcUpload() {
  const [file, setFile] = useState(null);
  const [mtd, setMtd] = useState(false);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(monthOptions()[1]);
  const [compliance, setCompliance] = useState(null);

  const loadHistory = useCallback(() => {
    api.get("/dpc/ach/uploads").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);
  useEffect(loadHistory, [loadHistory]);
  useEffect(() => {
    api.get(`/dpc/compliance?${qs({ month })}`).then(({ data }) => setCompliance(data)).catch(() => setCompliance(null));
  }, [month, summary]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mtd ? "mtd" : "daily");
      fd.append("as_of", asOf);
      const { data } = await api.post("/dpc/ach/upload", fd);
      setSummary(data);
      toast.success(`${data.rows_ok} rows loaded${data.rows_rejected ? `, ${data.rows_rejected} rejected` : ""}`);
      setFile(null);
      loadHistory();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="dpc-upload">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">DPC — Daily Achievement Upload</h1>

      <div className="border bg-card rounded-md p-5 space-y-3 max-w-2xl">
        <div className="flex items-center gap-3 flex-wrap">
          <UploadCloud size={18} className="text-muted-foreground" />
          <input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="dpc-ach-file" />
          <Button onClick={upload} disabled={!file || busy} data-testid="dpc-ach-upload-btn">{busy ? "Uploading…" : "Upload"}</Button>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <Checkbox checked={mtd} onCheckedChange={setMtd} data-testid="dpc-mtd-toggle" />
          This file contains MTD cumulative figures (system will diff against the previous upload)
        </label>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold">As-of date</span>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-8 border rounded px-2 text-xs" data-testid="dpc-asof-date" />
          <span>(used for DMS dumps like the LETBR source-wise sheet that carry no date column)</span>
        </div>
        <p className="text-[11px] text-muted-foreground">Accepts the LETBR source-wise/model-wise MTD dump as-is (auto-detected, processed in MTD-diff mode — models absent from the file are treated as unchanged, never zero) or long format: date | dealer_code | model | source | stage | ach_qty (also accepts qty / count).</p>
      </div>

      {summary && (
        <div className="border bg-card rounded-md p-5 space-y-2 max-w-3xl" data-testid="dpc-upload-summary">
          <h3 className="font-heading text-sm font-semibold flex items-center gap-2">
            {summary.rows_rejected ? <AlertTriangle size={15} className="text-amber-600" /> : <CheckCircle2 size={15} className="text-emerald-600" />}
            Upload summary — {summary.mode.toUpperCase()} mode{summary.format === "pivot_mtd" ? " · LETBR dump detected" : ""}
          </h3>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded bg-secondary px-2 py-1">Rows: {summary.rows_total}</span>
            <span className="rounded bg-emerald-100 text-emerald-800 px-2 py-1">Loaded: {summary.rows_ok}</span>
            <span className="rounded bg-red-100 text-red-700 px-2 py-1">Rejected: {summary.rows_rejected}</span>
            <span className="rounded bg-secondary px-2 py-1">Replaced: {summary.replaced}</span>
            <span className="rounded bg-secondary px-2 py-1">Dealers: {summary.dealers_covered.length}</span>
            <span className="rounded bg-secondary px-2 py-1">Dates: {summary.dates_covered.join(", ") || "—"}</span>
          </div>
          {summary.missing_dealers.length > 0 && (
            <p className="text-xs text-red-600" data-testid="dpc-summary-missing">No data for latest date ({summary.missing_dealers.length}): {summary.missing_dealers.join(", ")}</p>
          )}
          {summary.negatives.length > 0 && <div className="text-xs text-amber-700">{summary.negatives.map((n, i) => <div key={i}>⚠ {n}</div>)}</div>}
          {summary.warnings.length > 0 && <div className="text-xs text-amber-700">{summary.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>}
          {summary.rejected.length > 0 && (
            <div className="max-h-40 overflow-auto border rounded text-xs">
              {summary.rejected.map((r, i) => <div key={i} className="px-2 py-1 border-b last:border-0 text-red-700" data-testid="dpc-rejected-line">Line {r.line} · {r.dealer} {r.date || ""} — {r.reason}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="border bg-card rounded-md overflow-auto max-w-3xl" data-testid="dpc-upload-history">
        <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">Upload history</h3></div>
        <table className="w-full text-xs">
          <thead className="border-b"><tr>{["When", "File", "Mode", "By", "OK", "Rejected", "Dealers"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} className="border-b last:border-0">
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{h.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-1.5">
                  {h.file_name}
                  {h.status === "rolled_back" && <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-secondary text-muted-foreground" data-testid={`dpc-upload-rolled-back-${h.id}`}>rolled back</span>}
                </td>
                <td className="px-3 py-1.5 uppercase">{h.mode}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{h.by}</td>
                <td className="px-3 py-1.5 num">{h.rows_ok}</td>
                <td className={`px-3 py-1.5 num ${h.rows_rejected ? "text-red-600 font-bold" : ""}`}>{h.rows_rejected}</td>
                <td className="px-3 py-1.5 num">{h.dealers}</td>
              </tr>
            ))}
            {history.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">No uploads yet</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="border bg-card rounded-md overflow-auto" data-testid="dpc-compliance">
        <div className="px-4 py-3 border-b flex items-center gap-3">
          <h3 className="font-heading text-sm font-semibold">Upload compliance</h3>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-7 w-[120px] text-xs" data-testid="dpc-compliance-month"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          {compliance && <span className="text-[11px] text-muted-foreground">Late = first upload after {compliance.cutoff} next day</span>}
        </div>
        <table className="w-full text-xs">
          <thead className="border-b"><tr>{["Dealer", "Days", "Complete", "Late", "Missing", "Streak", "%"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {(compliance?.rows || []).map((r) => (
              <tr key={r.dealer_code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`dpc-compliance-${r.dealer_code}`}>
                <td className="px-3 py-1.5 font-medium whitespace-nowrap">{r.dealer_code} <span className="text-muted-foreground font-normal">{r.dealer_name}</span></td>
                <td className="px-3 py-1.5 num">{r.days}</td>
                <td className="px-3 py-1.5 num text-emerald-700 font-semibold">{r.complete}</td>
                <td className={`px-3 py-1.5 num ${r.late ? "text-amber-700 font-semibold" : ""}`}>{r.late}</td>
                <td className={`px-3 py-1.5 num ${r.missing ? "text-red-600 font-semibold" : ""}`} title={r.missing_dates?.join(", ")}>{r.missing}</td>
                <td className="px-3 py-1.5 num">{r.streak}</td>
                <td className={`px-3 py-1.5 num font-bold ${pctTone(r.pct)}`}>{fmtPct(r.pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
